import {
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import { MessageCircle02 } from '@untitled-ui/icons-react'
import Badge from '@mui/material/Badge'
import ButtonBase from '@mui/material/ButtonBase'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import { useIntercom } from 'react-use-intercom'
import useTranslate from '@/hooks/use-translate'
import {
  SUPPORT_CHAT_BUTTON_SIZE_PX,
  clampSupportChatButtonPosition,
  getIntercomSideNavWidth,
  getSupportChatButtonPosition,
  refreshSupportChatLayout,
  setSupportChatButtonPosition,
  useIntercomAlignment,
  useIntercomSideNavWidth,
  useIntercomUnreadCount,
  useSupportChatButtonPosition,
  type SupportChatButtonPosition,
} from '@/hooks/use-intercom-messenger'

/** En dessous, un appui suivi d'un léger tremblé reste un clic. */
const DRAG_THRESHOLD_PX = 4
const KEYBOARD_STEP_PX = 10
const KEYBOARD_LARGE_STEP_PX = 50

const KEYBOARD_MOVES: Record<string, { right: number; bottom: number }> = {
  ArrowLeft: { right: 1, bottom: 0 },
  ArrowRight: { right: -1, bottom: 0 },
  ArrowUp: { right: 0, bottom: 1 },
  ArrowDown: { right: 0, bottom: -1 },
}

const readWindowSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
})

/** Zone où le bouton peut aller : à droite de la sidenav. */
const readViewport = () => ({
  width: window.innerWidth - getIntercomSideNavWidth(),
  height: window.innerHeight,
})

type DragState = {
  pointerId: number
  startX: number
  startY: number
  origin: SupportChatButtonPosition
  moved: boolean
}

/**
 * Ouvre ou ferme la messagerie Intercom, dont la bulle par défaut est masquée : elle recouvrait
 * les boutons du coin bas droit. Plus petit que la bulle, il se déplace à la souris, au doigt ou
 * aux flèches, le long du bord droit ou du bord bas seulement, et la fenêtre s'ouvre du côté de
 * l'écran où il est posé.
 */
export const SupportChatButton: FC = () => {
  const { t: tCommon } = useTranslate('common')
  const { show, hide, isOpen } = useIntercom()
  const unreadCount = useIntercomUnreadCount()
  const alignment = useIntercomAlignment()
  const storedPosition = useSupportChatButtonPosition()
  const sideNavWidth = useIntercomSideNavWidth()
  const [windowSize, setWindowSize] = useState(readWindowSize)
  const [dragging, setDragging] = useState(false)
  const drag = useRef<DragState | null>(null)
  // Le clic qui suit le lâcher d'un glisser n'ouvre pas la messagerie.
  const swallowClick = useRef(false)

  useEffect(() => {
    const onResize = () => {
      setWindowSize(readWindowSize())
      refreshSupportChatLayout()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const position = clampSupportChatButtonPosition(storedPosition, {
    width: windowSize.width - sideNavWidth,
    height: windowSize.height,
  })

  const label = tCommon('supportChat')
  const ariaLabel =
    unreadCount > 0
      ? `${label} · ${tCommon('supportChatUnread', { count: unreadCount })}`
      : label

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: position,
      moved: false,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current
    if (!current || current.pointerId !== event.pointerId) return
    const dx = event.clientX - current.startX
    const dy = event.clientY - current.startY
    if (!current.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
      current.moved = true
      setDragging(true)
    }
    setSupportChatButtonPosition(
      clampSupportChatButtonPosition(
        {
          right: current.origin.right - dx,
          bottom: current.origin.bottom - dy,
        },
        readViewport()
      ),
      { persist: false }
    )
  }

  const endDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current
    if (!current || current.pointerId !== event.pointerId) return
    drag.current = null
    if (!current.moved) return
    swallowClick.current = true
    setDragging(false)
    setSupportChatButtonPosition(getSupportChatButtonPosition())
  }

  const handleClick = () => {
    if (swallowClick.current) {
      swallowClick.current = false
      return
    }
    if (isOpen) hide()
    else show()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const move = KEYBOARD_MOVES[event.key]
    if (!move) return
    event.preventDefault()
    const step = event.shiftKey ? KEYBOARD_LARGE_STEP_PX : KEYBOARD_STEP_PX
    setSupportChatButtonPosition(
      clampSupportChatButtonPosition(
        {
          right: position.right + move.right * step,
          bottom: position.bottom + move.bottom * step,
        },
        readViewport()
      )
    )
  }

  return (
    <Tooltip
      title={`${label} · ${tCommon('supportChatMoveHint')}`}
      placement={alignment === 'left' ? 'right' : 'left'}
      enterDelay={500}
      disableHoverListener={dragging}
      disableFocusListener={dragging}
    >
      <ButtonBase
        aria-label={ariaLabel}
        aria-pressed={isOpen}
        data-testid="support-chat-button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          position: 'fixed',
          right: position.right,
          bottom: position.bottom,
          // Sous les modales et les drawers, au-dessus du contenu.
          zIndex: (theme) => theme.zIndex.speedDial,
          width: SUPPORT_CHAT_BUTTON_SIZE_PX,
          height: SUPPORT_CHAT_BUTTON_SIZE_PX,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: (theme) => theme.shadows[dragging ? 12 : 6],
          cursor: dragging ? 'grabbing' : 'pointer',
          // Le glisser au doigt ne fait pas défiler la page.
          touchAction: 'none',
          userSelect: 'none',
          transition: (theme) =>
            theme.transitions.create(['box-shadow', 'background-color']),
          '&:hover': { bgcolor: 'primary.dark' },
          '&.Mui-focusVisible': {
            outline: (theme) => `2px solid ${theme.palette.primary.light}`,
            outlineOffset: 2,
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={9}
          sx={{
            '& .MuiBadge-badge': {
              top: -6,
              right: -6,
              minWidth: 16,
              height: 16,
              fontSize: 10,
              px: 0.5,
            },
          }}
        >
          <SvgIcon sx={{ fontSize: 22 }}>
            <MessageCircle02 />
          </SvgIcon>
        </Badge>
      </ButtonBase>
    </Tooltip>
  )
}
